import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Video, VideoOff, Mic, MicOff, Monitor, Phone, PhoneOff,
  Users, Plus, Calendar, Clock, Play, Square, Send,
  Settings, RefreshCw, User, MessageSquare, MoreVertical,
  Camera, Share2, UserPlus, X, Check, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideoConference() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  
  // Room state
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [createRoomOpen, setCreateRoomOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  
  // In-call state
  const [inCall, setInCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showChat, setShowChat] = useState(true);
  
  // Create room form
  const [newRoom, setNewRoom] = useState({
    name: '',
    description: '',
    type: 'care_team',
    scheduled_start: '',
    scheduled_end: '',
    max_participants: 10,
    recording_enabled: false,
    waiting_room_enabled: true,
    participants: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, roomsRes, typesRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/api/phase55/dashboard`),
        fetch(`${API_URL}/api/phase55/rooms`),
        fetch(`${API_URL}/api/phase55/rooms/types`),
        fetch(`${API_URL}/api/phase55/users`)
      ]);

      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setDashboard(data);
      }

      if (roomsRes.ok) {
        const data = await roomsRes.json();
        setRooms(data.rooms || []);
      }

      if (typesRes.ok) {
        const data = await typesRes.json();
        setRoomTypes(data.types || []);
      }

      if (usersRes.ok) {
        const data = await usersRes.json();
        setAvailableUsers(data.users || []);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load video conference data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewRoom = async (roomId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase55/rooms/${roomId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedRoom(data.room);
        setChatMessages(data.chat_messages || []);
        setRoomDialogOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load room details');
    }
  };

  const handleJoinRoom = async (roomId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase55/rooms/${roomId}/join`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Joined room successfully');
        setInCall(true);
        setActiveTab('call');
        await handleViewRoom(roomId);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to join room');
      }
    } catch (error) {
      toast.error('Failed to join room');
    }
  };

  const handleStartRoom = async (roomId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase55/rooms/${roomId}/start`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Room started successfully');
        setInCall(true);
        setActiveTab('call');
        await handleViewRoom(roomId);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start room');
      }
    } catch (error) {
      toast.error('Failed to start room');
    }
  };

  const handleEndRoom = async () => {
    if (!selectedRoom) return;
    
    try {
      const response = await fetch(`${API_URL}/api/phase55/rooms/${selectedRoom.id}/end`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Room ended');
        setInCall(false);
        setSelectedRoom(null);
        setActiveTab('dashboard');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to end room');
    }
  };

  const handleLeaveRoom = async () => {
    if (!selectedRoom) return;
    
    try {
      await fetch(`${API_URL}/api/phase55/rooms/${selectedRoom.id}/leave`, {
        method: 'POST'
      });
      setInCall(false);
      setSelectedRoom(null);
      setActiveTab('dashboard');
      toast.info('Left the room');
    } catch (error) {
      toast.error('Failed to leave room');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedRoom) return;
    
    try {
      const response = await fetch(`${API_URL}/api/phase55/rooms/${selectedRoom.id}/chat?message=${encodeURIComponent(newMessage)}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setChatMessages([...chatMessages, data.chat_message]);
        setNewMessage('');
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleCreateRoom = async () => {
    if (!newRoom.name || !newRoom.scheduled_start) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/phase55/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRoom)
      });

      if (response.ok) {
        toast.success('Room created successfully');
        setCreateRoomOpen(false);
        setNewRoom({
          name: '', description: '', type: 'care_team',
          scheduled_start: '', scheduled_end: '', max_participants: 10,
          recording_enabled: false, waiting_room_enabled: true, participants: []
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create room');
      }
    } catch (error) {
      toast.error('Failed to create room');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-700',
      scheduled: 'bg-blue-100 text-blue-700',
      ended: 'bg-slate-100 text-slate-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const colors = {
      idg_meeting: 'bg-purple-100 text-purple-700',
      family_conference: 'bg-teal-100 text-teal-700',
      care_team: 'bg-blue-100 text-blue-700',
      training: 'bg-orange-100 text-orange-700',
      one_on_one: 'bg-pink-100 text-pink-700'
    };
    const labels = {
      idg_meeting: 'IDG Meeting',
      family_conference: 'Family Conference',
      care_team: 'Care Team',
      training: 'Training',
      one_on_one: 'One-on-One'
    };
    return <Badge className={colors[type] || 'bg-slate-100'}>{labels[type] || type}</Badge>;
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="video-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-teal-600 mb-4" />
          <p className="text-slate-600">Loading video conference...</p>
        </div>
      </div>
    );
  }

  // In-Call View
  if (inCall && selectedRoom) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col" data-testid="video-call-view">
        {/* Video Grid */}
        <div className="flex-1 p-4 grid grid-cols-2 gap-4 max-h-[calc(100vh-140px)]">
          {selectedRoom.participants?.filter(p => p.status === 'joined').map((participant, idx) => (
            <div 
              key={participant.id} 
              className={`relative bg-slate-800 rounded-xl overflow-hidden ${
                idx === 0 ? 'col-span-2 row-span-2' : ''
              }`}
              data-testid={`participant-video-${participant.id}`}
            >
              {/* Mock video placeholder */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-3xl font-bold">
                  {participant.name.split(' ').map(n => n[0]).join('')}
                </div>
              </div>
              
              {/* Participant info overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium text-sm">{participant.name}</span>
                    {participant.role === 'host' && (
                      <Badge className="bg-teal-500 text-white text-xs">Host</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {participant.is_muted && <MicOff className="w-4 h-4 text-red-400" />}
                    {!participant.is_video_on && <VideoOff className="w-4 h-4 text-red-400" />}
                    {participant.is_screen_sharing && <Monitor className="w-4 h-4 text-green-400" />}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div className="absolute right-4 top-4 bottom-36 w-80 bg-white rounded-xl shadow-2xl flex flex-col">
            <div className="p-3 border-b flex items-center justify-between">
              <h3 className="font-semibold">Chat</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowChat(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {chatMessages.map((msg) => (
                <div key={msg.id} className={`${msg.type === 'system' ? 'text-center' : ''}`}>
                  {msg.type === 'system' ? (
                    <span className="text-xs text-slate-400">{msg.message}</span>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-slate-700">{msg.sender_name}</span>
                        <span className="text-xs text-slate-400">{new Date(msg.timestamp).toLocaleTimeString()}</span>
                      </div>
                      <p className="text-sm bg-slate-100 rounded-lg p-2">{msg.message}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="p-3 border-t flex gap-2">
              <Input 
                placeholder="Type a message..." 
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                className="flex-1"
              />
              <Button size="sm" onClick={handleSendMessage}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Room Info Bar */}
        <div className="bg-slate-800 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-white font-medium">{selectedRoom.name}</h2>
            {getTypeBadge(selectedRoom.type)}
            <span className="text-slate-400 text-sm">
              {selectedRoom.participants?.filter(p => p.status === 'joined').length} participants
            </span>
          </div>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Clock className="w-4 h-4" />
            {selectedRoom.actual_start && 'Started ' + formatTime(selectedRoom.actual_start)}
          </div>
        </div>

        {/* Control Bar */}
        <div className="bg-slate-800 border-t border-slate-700 p-4">
          <div className="flex items-center justify-center gap-4">
            <Button
              variant={isMuted ? 'destructive' : 'secondary'}
              size="lg"
              className="rounded-full w-14 h-14"
              onClick={() => setIsMuted(!isMuted)}
              data-testid="mute-btn"
            >
              {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </Button>
            
            <Button
              variant={isVideoOn ? 'secondary' : 'destructive'}
              size="lg"
              className="rounded-full w-14 h-14"
              onClick={() => setIsVideoOn(!isVideoOn)}
              data-testid="video-btn"
            >
              {isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
            </Button>
            
            <Button
              variant={isScreenSharing ? 'default' : 'secondary'}
              size="lg"
              className={`rounded-full w-14 h-14 ${isScreenSharing ? 'bg-green-600 hover:bg-green-700' : ''}`}
              onClick={() => setIsScreenSharing(!isScreenSharing)}
              data-testid="screenshare-btn"
            >
              <Monitor className="w-6 h-6" />
            </Button>
            
            <Button
              variant="secondary"
              size="lg"
              className="rounded-full w-14 h-14"
              onClick={() => setShowChat(!showChat)}
              data-testid="chat-btn"
            >
              <MessageSquare className="w-6 h-6" />
            </Button>
            
            <div className="w-px h-10 bg-slate-600 mx-2" />
            
            <Button
              variant="destructive"
              size="lg"
              className="rounded-full px-6"
              onClick={handleLeaveRoom}
              data-testid="leave-btn"
            >
              <PhoneOff className="w-5 h-5 mr-2" />
              Leave
            </Button>
            
            {selectedRoom.host_id === 'user_001' && (
              <Button
                variant="destructive"
                size="lg"
                className="rounded-full px-6"
                onClick={handleEndRoom}
                data-testid="end-btn"
              >
                <Square className="w-5 h-5 mr-2" />
                End for All
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="video-conference-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="video-title">
              <Video className="w-7 h-7 text-teal-600" />
              Video Conferencing
            </h1>
            <p className="text-slate-600 mt-1">Care team meetings and family conferences</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setCreateRoomOpen(true)} data-testid="create-room-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Meeting
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {dashboard && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Rooms</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="stat-active">{dashboard.statistics?.active_rooms || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Video className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">In Meetings</p>
                  <p className="text-2xl font-bold text-blue-600" data-testid="stat-participants">{dashboard.statistics?.active_participants || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Today's Meetings</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="stat-today">{dashboard.statistics?.scheduled_today || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Upcoming (24h)</p>
                  <p className="text-2xl font-bold text-teal-600" data-testid="stat-upcoming">{dashboard.statistics?.upcoming_24h || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Rooms Alert */}
      {dashboard?.active_rooms?.length > 0 && (
        <div className="mb-6">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="font-medium text-green-800">
                  {dashboard.active_rooms.length} meeting{dashboard.active_rooms.length > 1 ? 's' : ''} in progress
                </span>
                <div className="flex gap-2 ml-auto">
                  {dashboard.active_rooms.map(room => (
                    <Button 
                      key={room.id}
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleJoinRoom(room.id)}
                    >
                      Join: {room.name}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <Calendar className="w-4 h-4 mr-2" />
            Meetings
          </TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">
            <Clock className="w-4 h-4 mr-2" />
            Upcoming
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Video className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-4">
          {/* Upcoming Meetings */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg">Upcoming Meetings</CardTitle>
              <CardDescription>Your scheduled video conferences</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {rooms.filter(r => r.status === 'scheduled').map((room) => (
                  <div 
                    key={room.id} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50"
                    data-testid={`room-${room.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                        <Video className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800">{room.name}</h3>
                          {getTypeBadge(room.type)}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          {formatTime(room.scheduled_start)} • Host: {room.host_name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Users className="w-3 h-3 text-slate-400" />
                          <span className="text-xs text-slate-400">{room.participants?.length || 0} invited</span>
                          {room.patient_name && (
                            <>
                              <span className="text-slate-300">•</span>
                              <span className="text-xs text-teal-600">Patient: {room.patient_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleViewRoom(room.id)}>
                        Details
                      </Button>
                      <Button size="sm" onClick={() => handleStartRoom(room.id)} data-testid={`start-${room.id}`}>
                        <Play className="w-4 h-4 mr-1" />
                        Start
                      </Button>
                    </div>
                  </div>
                ))}
                {rooms.filter(r => r.status === 'scheduled').length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No upcoming meetings</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="upcoming">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg">Next 24 Hours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard?.upcoming_meetings?.map((room) => (
                  <div key={room.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h3 className="font-semibold">{room.name}</h3>
                      <p className="text-sm text-slate-500">{formatTime(room.scheduled_start)}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleViewRoom(room.id)}>
                      View
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg">Meeting History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {rooms.filter(r => r.status === 'ended').map((room) => (
                  <div key={room.id} className="flex items-center justify-between p-4 border rounded-lg opacity-75">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Video className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <h3 className="font-medium">{room.name}</h3>
                        <p className="text-sm text-slate-500">{formatTime(room.scheduled_start)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {room.recording_url && (
                        <Badge variant="outline" className="text-teal-600">Recording</Badge>
                      )}
                      {getStatusBadge(room.status)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Room Detail Dialog */}
      <Dialog open={roomDialogOpen} onOpenChange={setRoomDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedRoom?.name}</DialogTitle>
            <DialogDescription>{selectedRoom?.description}</DialogDescription>
          </DialogHeader>
          {selectedRoom && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {getTypeBadge(selectedRoom.type)}
                {getStatusBadge(selectedRoom.status)}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Scheduled</p>
                  <p className="font-medium">{formatTime(selectedRoom.scheduled_start)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Host</p>
                  <p className="font-medium">{selectedRoom.host_name}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">Participants ({selectedRoom.participants?.length || 0})</p>
                <div className="flex flex-wrap gap-2">
                  {selectedRoom.participants?.map(p => (
                    <Badge key={p.id} variant="outline" className="py-1">
                      <User className="w-3 h-3 mr-1" />
                      {p.name}
                      {p.role === 'host' && <span className="ml-1 text-teal-600">(Host)</span>}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                {selectedRoom.status === 'scheduled' && (
                  <Button className="flex-1" onClick={() => { setRoomDialogOpen(false); handleStartRoom(selectedRoom.id); }}>
                    <Play className="w-4 h-4 mr-2" />
                    Start Meeting
                  </Button>
                )}
                {selectedRoom.status === 'active' && (
                  <Button className="flex-1" onClick={() => { setRoomDialogOpen(false); handleJoinRoom(selectedRoom.id); }}>
                    <Video className="w-4 h-4 mr-2" />
                    Join Meeting
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Room Dialog */}
      <Dialog open={createRoomOpen} onOpenChange={setCreateRoomOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule New Meeting</DialogTitle>
            <DialogDescription>Create a video conference room</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Meeting Name *</Label>
              <Input
                value={newRoom.name}
                onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                placeholder="e.g., Weekly IDG Meeting"
                data-testid="new-room-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Meeting Type</Label>
              <Select value={newRoom.type} onValueChange={(v) => setNewRoom({ ...newRoom, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roomTypes.map((type) => (
                    <SelectItem key={type.type} value={type.type}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="datetime-local"
                  value={newRoom.scheduled_start}
                  onChange={(e) => setNewRoom({ ...newRoom, scheduled_start: e.target.value })}
                  data-testid="new-room-start"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="datetime-local"
                  value={newRoom.scheduled_end}
                  onChange={(e) => setNewRoom({ ...newRoom, scheduled_end: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newRoom.description}
                onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
                placeholder="Meeting agenda or notes..."
                rows={2}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="recording"
                  checked={newRoom.recording_enabled}
                  onCheckedChange={(c) => setNewRoom({ ...newRoom, recording_enabled: c })}
                />
                <Label htmlFor="recording" className="text-sm">Enable recording</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="waiting"
                  checked={newRoom.waiting_room_enabled}
                  onCheckedChange={(c) => setNewRoom({ ...newRoom, waiting_room_enabled: c })}
                />
                <Label htmlFor="waiting" className="text-sm">Waiting room</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateRoomOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateRoom} data-testid="create-room-submit">
              <Plus className="w-4 h-4 mr-2" />
              Create Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
